import { Link } from 'react-router-dom';
import { UserDashboardLayout } from '../../components/layout/UserDashboardLayout';
import { LoadingSpinner } from '../../components/layout';
import { useSavedJobs, useUnsaveJob } from '../../hooks/unified/useSavedJobsQueries';
import { useToast } from '../../contexts/ToastContext';
import { env } from '../../config/env';

// Helper function to get full image URL
const getImageUrl = (imageUrl: string | null | undefined): string | null => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  return `${env.API_BASE_URL}${imageUrl}`;
};

export function SavedJobsPage() {
  const { data: savedJobs, isLoading, error } = useSavedJobs();
  const unsaveJobMutation = useUnsaveJob();
  const { showSuccess, showError } = useToast();

  const handleUnsave = async (jobId: string, jobTitle: string | null) => {
    try {
      await unsaveJobMutation.mutateAsync(jobId);
      showSuccess('Job Removed', `${jobTitle || 'Job'} has been removed from your saved jobs.`);
    } catch {
      showError('Error', 'Failed to remove job from saved list.');
    }
  };

  const formatSalary = (min: number | null, max: number | null) => {
    if (min && max) {
      return `₹${min.toLocaleString()} - ₹${max.toLocaleString()}`;
    } else if (min) {
      return `From ₹${min.toLocaleString()}`;
    }
    return 'Competitive Salary';
  };

  const getJobTypeColor = (type: string | null) => {
    switch (type) {
      case 'remote':
        return 'bg-green-100 text-green-800';
      case 'contract':
        return 'bg-yellow-100 text-yellow-800';
      case 'part-time':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <UserDashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  if (error) {
    return (
      <UserDashboardLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            Failed to load saved jobs. Please try again later.
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-4 sm:p-6">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Saved Jobs</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Jobs you've saved for later. Click on a job to view details or apply.
          </p>
        </div>

        {savedJobs && savedJobs.length > 0 ? (
          <div className="grid gap-3 sm:gap-4">
            {savedJobs.map((savedJob) => (
              <div
                key={savedJob.id}
                className="bg-white rounded-lg shadow border border-gray-200 p-3 sm:p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Company Logo - Hidden on mobile */}
                  <div className="hidden sm:block">
                    {savedJob.company_logo_url && getImageUrl(savedJob.company_logo_url) ? (
                      <img
                        src={getImageUrl(savedJob.company_logo_url)!}
                        alt={`${savedJob.company} logo`}
                        className="w-12 h-12 rounded-lg object-cover bg-gray-100 flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Job Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                      <div className="min-w-0">
                        <Link
                          to={`/jobs/${savedJob.job_id}`}
                          className="text-base sm:text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors line-clamp-2"
                        >
                          {savedJob.title || 'Untitled Job'}
                        </Link>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-gray-600">
                          {savedJob.company && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                              <span className="truncate max-w-[120px] sm:max-w-none">{savedJob.company}</span>
                            </span>
                          )}
                          {savedJob.location && (
                            <span className="flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="truncate max-w-[100px] sm:max-w-none">{savedJob.location}</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions - Stacked on mobile */}
                      <div className="flex items-center gap-2 flex-shrink-0 mt-2 sm:mt-0">
                        <Link
                          to={`/jobs/${savedJob.job_id}`}
                          className="flex-1 sm:flex-none px-3 py-2 min-h-[44px] bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors text-center flex items-center justify-center"
                        >
                          View Job
                        </Link>
                        <button
                          onClick={() => handleUnsave(savedJob.job_id, savedJob.title)}
                          disabled={unsaveJobMutation.isPending}
                          className="p-2 min-w-[44px] min-h-[44px] text-gray-400 hover:text-red-500 transition-colors flex items-center justify-center"
                          title="Remove from saved"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Tags and Salary */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 sm:mt-3">
                      {savedJob.job_type && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getJobTypeColor(savedJob.job_type)}`}>
                          {savedJob.job_type.replace('-', ' ').toUpperCase()}
                        </span>
                      )}
                      {savedJob.status && savedJob.status !== 'active' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          {savedJob.status.toUpperCase()}
                        </span>
                      )}
                      <span className="text-xs sm:text-sm font-medium text-gray-900">
                        {formatSalary(savedJob.salary_min, savedJob.salary_max)}
                      </span>
                    </div>

                    {/* Saved date */}
                    <div className="mt-2 text-xs text-gray-500">
                      Saved {new Date(savedJob.saved_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6 sm:p-8 text-center">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No saved jobs yet</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              Browse jobs and click the heart icon to save them for later.
            </p>
            <Link
              to="/jobs"
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 min-h-[44px] bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Browse Jobs
            </Link>
          </div>
        )}
      </div>
    </UserDashboardLayout>
  );
}
